import { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { csv } from 'd3-fetch'
import { MediaQueries as Mq } from '../../styles/settings/theme'
import { Theme } from '../../styles/settings/theme'
import { Flex, Content } from '../atoms/Basics'
import ChartTitle from '../atoms/ChartTitle'
import ChartContainer from '../atoms/ChartContainer'
import PercentTickAxis from '../atoms/PercentTickAxis'
import LegendColors from '../atoms/LegendColors'
import Subtitle from '../atoms/Subtitle'
import YearSelector from '../atoms/yearSelector'

const RowFlex = styled(Flex)`
  align-items: center;
  margin: 0 1rem 0.75rem;
`
const Label = styled.div`
  color: ${(props) => props.theme.colors.gray};
  margin: 0 1rem 0 0;
  //min-width: 16rem;
  width: 17rem;
  font-size: ${(props) => props.theme.fontSizes[0]};
  font-weight: 600;
  text-align: right;
  ${Mq.md} {
    font-size: 12px;
    width: 11rem;
  }
  ${Mq.sm} {
    font-size: 11px;
    width: 9rem;
  }
  ${Mq.xs} {
    font-size: 10px;
    width: 7.5rem;
  }
`
const BaseBar = styled.div`
  flex: 1;
  height: 2.25rem;
  background: ${(props) => props.gradient || props.theme.colors.gray0};
  position: relative;
`

const TickContainer = styled.div`
  flex: 1;
  position: relative;
  height: 7rem;
`

const ContentWrapper = styled(Content)`
  width: 100%;
`

const Challenges = () => {
  const $ = window.WILDLABS_VARS
  const [allData, setAllData] = useState(null)
  const [data, setData] = useState(null)
  const [availableYears, setAvailableYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('All Years')

  useEffect(() => {
    csv(process.env.PUBLIC_URL + '/data/challenges.csv').then((rawData) => {
      setAllData(rawData)

      // Extract unique years from the data
      const years = [...new Set(rawData.map((row) => row.year))].sort(
        (a, b) => {
          if (a === 'All Years') return -1
          if (b === 'All Years') return 1
          return a.localeCompare(b)
        }
      )
      setAvailableYears(years)
    })
  }, [])

  useEffect(() => {
    if (allData && selectedYear) {
      const filteredData = allData.filter((row) => {
        // Filter by selected year
        if (row.year !== selectedYear) return false

        // Filter out rows with NA values in any of the ranking columns
        const rankingColumns = [
          'Ranked 1st by respondents',
          '2nd',
          '3rd',
          '4th',
          '5th',
          '6th',
          '7th',
          '8th',
          '9th',
          '10th',
          'Average rank'
        ]

        const hasNAValues = rankingColumns.some(
          (col) =>
            row[col] === 'NA' || row[col] === '' || row[col] === undefined
        )

        return !hasNAValues
      })

      setData(filteredData)
    }
  }, [allData, selectedYear])

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value)
  }
  const colors = [
    Theme.colors.darkOrange,
    Theme.colors.mediumOrange,
    Theme.colors.lightOrange,
    Theme.colors.veryLightOrange,
    Theme.colors.gray5,
    Theme.colors.gray4,
    Theme.colors.gray3,
    Theme.colors.gray2,
    Theme.colors.gray1,
    Theme.colors.gray0
  ]

  const allBars = [
    $.challenges_columns.First,
    $.challenges_columns.Second,
    $.challenges_columns.Third,
    $.challenges_columns.Fourth,
    $.challenges_columns.Fifth,
    $.challenges_columns.Sixth,
    $.challenges_columns.Seventh,
    $.challenges_columns.Eighth,
    $.challenges_columns.Ninth,
    $.challenges_columns.Tenth
    //CHALLENGES_COLUMNS.Ninth -- not showing this just lets remainder be base bar color (so you won't see bar that adds up to over 100)
  ]

  const dataSorted = data?.sort((a, b) => {
    return a[$.challenges_columns.Eleventh] - b[$.challenges_columns.Eleventh]
  })

  // Don't render until data is loaded
  if (!data || !allData) {
    return (
      <ChartContainer>
        <ChartTitle text={$.challenges_titles.chartTitle} />
        <div>Loading...</div>
      </ChartContainer>
    )
  }

  return (
    <ChartContainer>
      <ChartTitle text={$.challenges_titles.chartTitle}>
        <YearSelector value={selectedYear} onChange={handleYearChange}>
          {availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </YearSelector>
      </ChartTitle>

      <ContentWrapper flexDirection="column">
        <RowFlex justifyContent="space-between">
          <LegendColors colors={colors} columns={allData.columns.slice(2)} />
        </RowFlex>
        <RowFlex>
          <Label></Label>
          <TickContainer>
            <Flex justifyContent="center">
              <Subtitle
                text={$.challenges_titles.subtitle}
                size={Theme.fontSizes[2]}
                color={Theme.colors.gray}
              ></Subtitle>
            </Flex>
            <PercentTickAxis />
          </TickContainer>
        </RowFlex>
        {dataSorted?.map((row, i) => {
          // Create CSS gradient string for seamless color transitions
          const gradientParts = []
          let cumulativePosition = 0

          colors.forEach((color, segmentIndex) => {
            const rawWidth = allBars[segmentIndex]
              ? +row[allBars[segmentIndex]] || 0
              : 0
            const widthPercent = rawWidth * 100

            if (widthPercent > 0) {
              const startPercent = cumulativePosition
              const endPercent = cumulativePosition + widthPercent

              // Add color stop at start and end positions
              gradientParts.push(`${color} ${startPercent.toFixed(2)}%`)
              gradientParts.push(`${color} ${endPercent.toFixed(2)}%`)

              cumulativePosition += widthPercent
            }
          })

          // Create linear gradient string
          const gradient =
            gradientParts.length > 0
              ? `linear-gradient(to right, ${gradientParts.join(', ')})`
              : Theme.colors.gray0

          return (
            <RowFlex key={row.challenge}>
              <Label>{row.challenge}</Label>
              <BaseBar gradient={gradient} />
            </RowFlex>
          )
        })}
      </ContentWrapper>
    </ChartContainer>
  )
}

export default Challenges
